import { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';
import { WebApp } from '../utils/webAppShim';
import SensitiveContent from './SensitiveContent';
import { applyLoadingBackground } from '../utils/loadingBackground';

const RegisterForm = ({ apiUrl, onSuccess }) => {
    const { registrationAllowed, verification, tempUser, deviceId, blockSession } = useUser();
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [wrongAttempts, setWrongAttempts] = useState(0);
    const [isBlocked, setIsBlocked] = useState(false);
    const [isPasswordCorrect, setIsPasswordCorrect] = useState(false);
    const [requestSent, setRequestSent] = useState(false);
    const [existingRequest, setExistingRequest] = useState(null);
    const [checkingStatus, setCheckingStatus] = useState(true);

    const [formData, setFormData] = useState({
        firstName: tempUser?.first_name || "",
        lastName: tempUser?.last_name || "",
        middleName: "",
    });

    const handleSupportLeadtehClick = () => {
        WebApp.openLink("https://app.leadteh.ru/w/djk3S", {
            try_instant_view: false,
            try_browser: 'default'
        });
    };

    useEffect(() => {
        const checkExistingRequest = async () => {
            if (!tempUser?.legalPersonId || !tempUser?.id) {
                setCheckingStatus(false);
                return;
            }

            try {
                const response = await fetch(
                    `${apiUrl}/front/registration-requests/status?telegramId=${tempUser.id}&legalPerson=${tempUser.legalPersonId}`
                );
                const data = await response.json();

                if (data.hasRequest) {
                    setExistingRequest(data);
                    if (data.status === 'pending') {
                        setRequestSent(true);
                    }
                }
            } catch (error) {
                console.error("Ошибка проверки статуса заявки:", error);
            } finally {
                setCheckingStatus(false);
            }
        };

        checkExistingRequest();
    }, [apiUrl, tempUser?.id, tempUser?.legalPersonId]);

    if (!registrationAllowed) {
        return (
            <div className="container min-h-screen flex flex-col items-center justify-center p-4 text-white">
                <p className="text-center">Регистрация недоступна</p>
            </div>
        );
    }

    if (!tempUser) {
        return (
            <div className="container min-h-screen flex flex-col items-center justify-center p-4 text-white">
                <p className="text-center">Ошибка: данные Telegram не найдены.</p>
            </div>
        );
    }

    if (checkingStatus) {
        return (
            <div className="container min-h-screen flex flex-col items-center justify-center p-4 text-white">
                <div className="text-center p-6 rounded-xl bg-gray-900/80 backdrop-blur-md shadow-lg">
                    <p>Загрузка...</p>
                </div>
            </div>
        );
    }

    const LOG = (step, ...args) => console.log('[PIN_FLOW]', step, ...args);

    const handlePasswordCheck = async () => {
        if (isBlocked) {
            setError("Слишком много попыток вы заблокированы.");
            return;
        }
        setError(null);
        LOG('1. Start');

        try {
            const url1 = `${apiUrl}/front/users`;
            LOG('2. Request 1 (check PIN):', url1);
            const response = await fetch(url1, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    telegramId: tempUser.id,
                    firstName: tempUser.first_name,
                    lastName: tempUser.last_name,
                    username: tempUser.username,
                    password,
                    deviceId,
                    legalPerson: tempUser.legalPersonId,
                }),
            });

            LOG('3. Response 1: status=', response.status, 'ok=', response.ok);
            let data = {};
            try {
                const text = await response.text();
                LOG('3b. Response 1 body length=', text.length, 'preview=', text.slice(0, 150));
                data = text ? JSON.parse(text) : {};
            } catch (_) {
                LOG('3c. Parse FAILED');
                throw new Error("Ответ сервера не распознан. Попробуйте ещё раз.");
            }

            if (!response.ok) {
                const err = typeof data.error === 'string' ? data.error : "Неверный пароль";
                LOG('4. Response 1 not OK, throw:', err);
                throw new Error(err);
            }

            if (data.panicTriggered) {
                LOG('5a. panicTriggered — block session immediately');
                try {
                    WebApp.showAlert("Доступ ограничен по соображениям безопасности.");
                } catch (_) {
                    /* blockSession shows full-screen deny */
                }
                blockSession("Доступ ограничен по соображениям безопасности.");
                return;
            }

            LOG('5. passwordVerified=', data.passwordVerified, 'verification=', verification);
            if (typeof data.legalPersonMiniAppBackgroundUrl === "string" && data.legalPersonMiniAppBackgroundUrl.trim()) {
                applyLoadingBackground(data.legalPersonMiniAppBackgroundUrl.trim());
            }
            if (data.passwordVerified) {
                setIsPasswordCorrect(true);
                setError(null);
                if (verification) {
                    LOG('6. Call verificationUser');
                    try {
                        await verificationUser(tempUser);
                        LOG('7. verificationUser OK, call onSuccess()');
                        onSuccess();
                    } catch (verErr) {
                        const msg = (verErr && verErr.message && String(verErr.message).trim()) || "Ошибка верификации. Попробуйте ещё раз.";
                        LOG('7. CATCH verificationUser:', String(verErr?.message), 'type=', typeof verErr?.message);
                        setError(msg);
                        return;
                    }
                }
            }

        } catch (error) {
            LOG('8. OUTER CATCH:', String(error?.message), 'type=', typeof error?.message);
            setError((error && typeof error.message === 'string') ? error.message : "Ошибка. Попробуйте ещё раз.");
            setWrongAttempts((prev) => prev + 1); 

            if (wrongAttempts + 1 >= 3) { 
                lockUsers(tempUser); 
                setIsBlocked(true); 
                setWrongAttempts(0);
                setError(null);
            }
        }
        finally {
            setPassword("");
        }
    };

    const lockUsers = async (user) => {
        try {
            const response = await fetch(`${apiUrl}/front/users/lock`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    telegramId: user.id,
                    firstName: user.first_name,
                    lastName: user.last_name,
                    username: user.username,
                    legalPerson: user.legalPersonId,
                }),
            });
    
            const data = await response.json();
    
            if (!response.ok) {
                throw new Error(data.error || "Ошибка логирования попытки.");
            }
    
            console.log("✅ Логирование неудачной попытки отправлено на сервер.");
        } catch (error) {
            console.error("❌ Ошибка логирования неудачной попытки:", error);
        }
    };

    const verificationUser = async (user) => {
        const payload = { telegramId: String(user?.id ?? ''), deviceId: deviceId || '' };
        const url = `${apiUrl}/front/users/verification`;
        LOG('  verificationUser: url=', url, 'payload.telegramId=', payload.telegramId);
        let raw, data = {}, response;
        try {
            response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            raw = await response.text();
            LOG('  verificationUser: status=', response.status, 'body length=', raw?.length, 'body=', raw?.slice(0, 200));
            try {
                data = raw ? JSON.parse(raw) : {};
            } catch (_) {
                data = {};
            }
        } catch (e) {
            LOG('  verificationUser: NETWORK ERROR', e?.message);
            const networkMsg = (e && typeof e.message === 'string') ? e.message : 'Нет связи с сервером.';
            throw new Error(networkMsg);
        }
        if (!response.ok) {
            const msg = typeof data.error === 'string' ? data.error : `Ошибка ${response.status}. Попробуйте ещё раз.`;
            LOG('  verificationUser: NOT OK, throw:', msg);
            throw new Error(msg);
        }
        LOG('  verificationUser: OK 200');
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmitRequest = async () => {
        if (!formData.lastName?.trim()) {
            setError("Введите фамилию");
            return;
        }
        if (!formData.firstName?.trim()) {
            setError("Введите имя");
            return;
        }

        setError(null);

        try {
            const response = await fetch(`${apiUrl}/front/registration-requests`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    telegramId: tempUser.id,
                    username: tempUser.username,
                    deviceId: deviceId,
                    legalPerson: tempUser.legalPersonId,
                    firstName: formData.firstName.trim(),
                    lastName: formData.lastName.trim(),
                    middleName: formData.middleName?.trim() || "",
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Ошибка отправки заявки");
            }

            setRequestSent(true);
        } catch (error) {
            setError(error.message);
        }
    };

    if (requestSent) {
        return (
            <div className="container min-h-screen flex flex-col items-center justify-center p-4 text-white">
                <div className="text-center p-6 max-w-md rounded-xl bg-gray-900/80 backdrop-blur-md shadow-lg">
                    <div className="text-5xl mb-4">✓</div>
                    <h2 className="text-xl font-bold mb-2">Заявка отправлена</h2>
                    <p className="text-gray-400 mb-4">
                        Ваша заявка на регистрацию отправлена администратору. 
                        После одобрения вы сможете войти в систему.
                    </p>
                    <button
                        onClick={handleSupportLeadtehClick}
                        className="mt-4 px-4 py-2 rounded-md bg-blue-500 hover:bg-blue-600 transition-colors"
                    >
                        Техподдержка
                    </button>
                </div>
            </div>
        );
    }

    if (existingRequest?.status === 'rejected') {
        return (
            <div className="container min-h-screen flex flex-col items-center justify-center p-4 text-white">
                <div className="text-center p-6 max-w-md rounded-xl bg-gray-900/80 backdrop-blur-md shadow-lg">
                    <div className="text-5xl mb-4 text-red-500">✕</div>
                    <h2 className="text-xl font-bold mb-2">Заявка отклонена</h2>
                    <p className="text-gray-400 mb-2">
                        Ваша заявка на регистрацию была отклонена.
                    </p>
                    {existingRequest.rejectionReason && (
                        <p className="text-gray-300 mb-4">
                            Причина: {existingRequest.rejectionReason}
                        </p>
                    )}
                    <button
                        onClick={handleSupportLeadtehClick}
                        className="mt-4 px-4 py-2 rounded-md bg-blue-500 hover:bg-blue-600 transition-colors"
                    >
                        Техподдержка
                    </button>
                </div>
            </div>
        );
    }

    if (verification) {
        return (
        <SensitiveContent showWarning warningText="Не передавайте PIN третьим лицам и не делайте скриншоты.">
        <div className="container min-h-screen flex flex-col items-center justify-center gap-4 p-4 text-white">
        
            <h2 className="text-xl text-center font-semibold">Введите PIN для верификации и добавления нового устройства.</h2>           
            <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isBlocked}
                className="mt-4 p-2 border border-gray-600 rounded-md text-black"
            />
            <h4 className="text-base text-center font-semibold">Если вы не знаете PIN-код, обратитесь к администратору</h4>
            
            {error && <p className="text-red-500 text-center mt-2">{error}</p>}
            {isBlocked && <p className="text-red-500 text-center mt-2">Превышенно количество попыток ввода! Ваш аккаунт заблокирован обратитесь к администратору.</p>}
            <button
                onClick={handlePasswordCheck}
                disabled={isBlocked} 
                className={`mt-4 px-4 py-2 rounded-md ${isBlocked ? "bg-gray-600" :(isPasswordCorrect ? "bg-green-500" : "bg-blue-500")}`}
            >
                {isBlocked ? "Заблокировано" :(isPasswordCorrect ? "PIN верный" : "Проверить PIN")}
            </button>
            {error && isPasswordCorrect && (
                <button type="button" onClick={() => window.location.reload()} className="mt-4 px-4 py-2 rounded-md bg-blue-500">
                    Обновить и войти
                </button>
            )}
            <button
                    onClick={handleSupportLeadtehClick}
                    className=" mt-4 px-4 py-2 rounded-md bg-blue-500 hover:bg-blue-600 transition-colors"
                >
                    Техподдержка
            </button>
        </div>
        </SensitiveContent>
        );
    }

    if (!tempUser.legalPersonId) {
		return (
			<div className="container min-h-screen flex flex-col items-center justify-center p-4 text-white">
				<div className="text-center p-6 max-w-md rounded-xl bg-gray-900/80 backdrop-blur-md shadow-lg">
					<h2 className="text-xl font-bold">Недопустимая ссылка</h2>
					<p className="mt-2">Регистрация доступна только по персональной ссылке.</p>
				</div>
			</div>
		);
	}

    return (
        <SensitiveContent showWarning warningText="Не передавайте пароль третьим лицам и не делайте скриншоты.">
        <div className="container min-h-screen flex flex-col items-center justify-center gap-4 p-4 text-white">
            {!isPasswordCorrect && <>
            <h2 className="text-xl font-semibold">Введите пароль для регистрации</h2>           
            <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isBlocked}
                className="mt-4 p-2 border border-gray-600 rounded-md text-black"
            />
            <h4 className="text-base text-center font-semibold">Если вы не знаете PIN-код, обратитесь к администратору</h4>
            </>}
            {error && password === "" && !isPasswordCorrect && <p className="text-red-500 text-center mt-2">{error}</p>}
            {isBlocked && <p className="text-red-500 text-center mt-2">Превышенно количество попыток ввода! Ваш аккаунт заблокирован обратитесь к администратору.</p>}
            <button
                onClick={handlePasswordCheck}
                disabled={isBlocked} 
                className={`mt-4 px-4 py-2 rounded-md ${isBlocked ? "bg-gray-600" :(isPasswordCorrect ? "bg-green-500" : "bg-blue-500")}`}
            >
                {isBlocked ? "Заблокировано" :(isPasswordCorrect ? "Пароль верный" : "Зарегистрироваться")}
            </button>
            {!isPasswordCorrect && <button
                    onClick={handleSupportLeadtehClick}
                    className=" mt-4 px-4 py-2 rounded-md bg-blue-500 hover:bg-blue-600 transition-colors"
                >
                    Техподдержка
            </button>}

            {isPasswordCorrect && <>
                <h2 className="text-xl font-semibold">Заполните данные для заявки</h2>
                <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Фамилия *"
                    className="mt-2 p-2 w-full sm:w-96 border border-gray-600 rounded-md text-black"
                />
                <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Имя *"
                    className="mt-2 p-2 w-full sm:w-96 border border-gray-600 rounded-md text-black"
                />
                <input
                    type="text"
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleInputChange}
                    placeholder="Отчество"
                    className="mt-2 p-2 w-full sm:w-96 border border-gray-600 rounded-md text-black"
                />
                {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                <button
                    onClick={handleSubmitRequest}
                    className="mt-4 px-4 py-2 bg-blue-500 rounded-md hover:bg-blue-600 transition-colors"
                >
                    Отправить заявку
                </button>
            </>}
        </div>
        </SensitiveContent>
    );
};

export default RegisterForm;
